// Desktop Threads tab — list view.
//
// Route: /threads. Renders the current user's threads (newest-active-first),
// each row links to /threads/:threadId for the detail experience that
// mirrors the iOS app's Home → Thread Detail flow.
//
// Backend: GET /api/v1/threads (paginated). v1 fetches the first 50 with no
// pagination UI — flag for follow-up if users routinely exceed that.

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Spinner,
  Button,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import CreateThreadDialog from "./CreateThreadDialog";
import { listThreads } from "../services/embed";
import { getTestAudioFeatureStatus } from "../services/testAudioService";
import { formatDate } from "../utils/dateUtils";

const ThreadsListPage = ({
  // Test injection points (default to the real service).
  featureStatusFn,
}) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // null = unknown / still checking; true/false = resolved. We only render the
  // "+ Create Thread" button when this is true (Ethosphere org on dev).
  const [createEnabled, setCreateEnabled] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const doFeatureStatus = featureStatusFn || getTestAudioFeatureStatus;

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listThreads({ limit: 50 });
      // Backend returns `{ items: [...], next_cursor }` per PRD §8.2.
      const items = response?.items || (Array.isArray(response) ? response : []);
      setThreads(items);
    } catch (e) {
      const message =
        e?.response?.data?.detail || e?.message || "Failed to load threads.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Gate the "+ Create Thread" button behind the same Test Audio feature
  // flag — non-Ethosphere orgs (and prod) will get `{enabled: false}` and
  // see only the disabled "New Thread" tooltip button.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { enabled } = await doFeatureStatus();
        if (!cancelled) setCreateEnabled(!!enabled);
      } catch {
        if (!cancelled) setCreateEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doFeatureStatus]);

  return (
    <PageLayout>
      <VStack spacing={5} align="stretch" p={6} data-testid="threads-list-page">
        {/* Header toolbar */}
        <Flex justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold">
            Threads
          </Text>
          <HStack spacing={2}>
            {createEnabled && (
              <Button
                colorScheme="blue"
                size="sm"
                px={5}
                borderRadius="md"
                fontWeight="semibold"
                leftIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                data-testid="threads-create-button"
              >
                Create Thread
              </Button>
            )}
            <Tooltip
              hasArrow
              label="Recording is mobile-only — start new threads from the iOS app. Use the Test Audio tab to seed threads from saved audio files."
              placement="bottom-end"
            >
              <Button
                colorScheme="blue"
                size="sm"
                px={5}
                borderRadius="md"
                fontWeight="semibold"
                leftIcon={<AddIcon />}
                isDisabled
                data-testid="threads-new-button"
              >
                New Thread
              </Button>
            </Tooltip>
          </HStack>
        </Flex>

        {loading && (
          <Flex justify="center" py={16}>
            <Spinner size="lg" />
          </Flex>
        )}

        {!loading && error && (
          <Alert status="error" borderRadius="md" data-testid="threads-error">
            <AlertIcon />
            <Box flex={1}>
              <AlertTitle fontSize="sm">Could not load threads</AlertTitle>
              <AlertDescription fontSize="xs">{error}</AlertDescription>
            </Box>
            <Button size="xs" onClick={fetchThreads}>
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && threads.length === 0 && (
          <Box
            py={16}
            textAlign="center"
            bg="gray.50"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            borderStyle="dashed"
            data-testid="threads-empty"
          >
            <Text color="gray.500" fontSize="sm">
              No recordings yet. Use the iOS app to start recording.
            </Text>
          </Box>
        )}

        {!loading && !error && threads.length > 0 && (
          <Box
            overflowX="auto"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.200"
            bg="white"
          >
            <Table variant="unstyled" size="sm">
              <Thead>
                <Tr
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderBottomColor="gray.200"
                >
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Title
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Created
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" isNumeric>
                    Recordings
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Last Recorded
                  </Th>
                  <Th py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                    Latest Analysis
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {threads.map((t) => (
                  <Tr
                    key={t.id}
                    data-testid={`thread-row-${t.id}`}
                    cursor="pointer"
                    onClick={() => navigate(`/threads/${t.id}`)}
                    borderBottomWidth="1px"
                    borderBottomColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    transition="background 0.1s"
                  >
                    <Td py={3} px={4}>
                      <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="medium">
                          {t.title || "Untitled"}
                        </Text>
                        {t.archived && (
                          <Badge colorScheme="gray" fontSize="xs">
                            Archived
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(t.created_at)}
                      </Text>
                    </Td>
                    <Td py={3} px={4} isNumeric>
                      <Text fontSize="sm">
                        {t.recording_count ?? 0}
                      </Text>
                    </Td>
                    <Td py={3} px={4}>
                      <Text fontSize="sm" color="gray.600">
                        {t.last_recorded_at
                          ? formatDate(t.last_recorded_at)
                          : "—"}
                      </Text>
                    </Td>
                    <Td py={3} px={4}>
                      {t.last_analyzed_at ? (
                        <Text fontSize="sm" color="gray.600">
                          {formatDate(t.last_analyzed_at)}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.400">
                          —
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      <CreateThreadDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </PageLayout>
  );
};

export default ThreadsListPage;
